import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { TrendUp, Receipt, Wallet, WarningCircle, ArrowRight } from "@phosphor-icons/react";

export default function Dashboard() {
  const { company } = useAuth();
  const [kpi, setKpi] = useState(null);

  useEffect(() => {
    if (!company) return;
    api.get("/dashboard/kpis").then(({ data }) => setKpi(data)).catch(() => {});
  }, [company]);

  if (!company) {
    return (
      <div className="lb-card p-10 text-center" data-testid="no-company-banner">
        <div className="font-display font-extrabold text-2xl">Set up your company first</div>
        <p className="text-neutral-600 mt-2">Enter your GSTIN to start invoicing.</p>
        <Link to="/app/company-setup" className="lb-btn mt-6 inline-flex" data-testid="cta-company-setup">Setup Company →</Link>
      </div>
    );
  }

  const KPI = [
    { label: "Total Revenue", value: kpi?.total_revenue, icon: TrendUp, tid: "kpi-revenue", cls: "lb-kpi-revenue" },
    { label: "GST Payable", value: kpi?.gst_payable, icon: Receipt, tid: "kpi-gst", cls: "lb-kpi-gst" },
    { label: "Outstanding", value: kpi?.outstanding, icon: WarningCircle, tid: "kpi-outstanding", cls: "lb-kpi-outstanding" },
    { label: "Cash Position", value: kpi?.cash_position, icon: Wallet, tid: "kpi-cash", cls: "lb-kpi-cash" },
  ];

  return (
    <div className="space-y-6 lb-fade" data-testid="dashboard-page">
      <header className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Dashboard</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">{company.name}</h1>
          <div className="font-mono text-xs text-neutral-500 mt-1">{company.gstin} · {company.state}</div>
        </div>
        <Link to="/app/invoices/new" className="lb-btn" data-testid="new-invoice-btn">
          New Invoice <ArrowRight size={14} />
        </Link>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {KPI.map(({ label, value, icon: Icon, tid, cls }) => (
          <div key={label} className={"lb-kpi " + (cls || "")} data-testid={tid}>
            <div className="flex items-center justify-between">
              <div className="lb-kpi-label">{label}</div>
              <Icon size={16} className="text-neutral-400" />
            </div>
            <div className="lb-kpi-value mono">{fmtINR(value || 0)}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lb-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-bold text-lg">Revenue trend</div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-mono">last 6 months</div>
          </div>
          <div className="h-64" data-testid="revenue-chart">
            {kpi?.monthly_revenue?.length ? (
              <ResponsiveContainer>
                <LineChart data={kpi.monthly_revenue}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#E4E4E7" />
                  <XAxis dataKey="month" stroke="#71717A" fontSize={11} />
                  <YAxis stroke="#71717A" fontSize={11} tickFormatter={(v) => "₹" + v.toLocaleString("en-IN")} />
                  <Tooltip contentStyle={{ background: "#0A66C2", color: "#fff", borderRadius: 2, border: "none", fontSize: 12 }}
                           formatter={(v) => fmtINR(v)} />
                  <Line type="monotone" dataKey="revenue" stroke="#0A66C2" strokeWidth={2} dot={{ r: 3, fill: "#0A66C2" }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-neutral-500">No invoices yet</div>
            )}
          </div>
        </div>

        <div className="lb-card p-5">
          <div className="font-display font-bold text-lg mb-4">Recent invoices</div>
          <div className="space-y-3" data-testid="recent-invoices">
            {(kpi?.recent_invoices || []).map((i) => (
              <Link key={i.id} to={`/app/invoices/${i.id}`}
                    className="flex items-center justify-between border-b border-neutral-100 pb-2 hover:bg-neutral-50 px-1 transition-colors">
                <div>
                  <div className="font-mono text-xs">{i.invoice_no}</div>
                  <div className="text-xs text-neutral-500">{i.customer_snapshot?.name}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-semibold text-sm">{fmtINR(i.total)}</div>
                  <div className={"text-[10px] uppercase tracking-wide font-semibold " +
                    (i.status === "paid" ? "text-emerald-600" : i.status === "partial" ? "text-amber-600" : "text-rose-600")}>
                    {i.status}
                  </div>
                </div>
              </Link>
            ))}
            {!kpi?.recent_invoices?.length && (
              <div className="text-sm text-neutral-500">No invoices yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
